import { Link } from "react-router-dom";
import ServiceLayout, { ContentSection } from "@/components/site/ServiceLayout";
import { Reveal, TickList } from "@/components/site/Reveal";

const WHO = [
  "Primary victims of violent crime in NSW",
  "Immediate family members of victims",
  "Witnesses to violent crime",
  "Parents and carers of child victims",
];

const STEPS = [
  {
    n: "01",
    title: "Apply through Victims Services",
    text: "Contact the Victims Access Line on 1800 633 063 or apply online to request counselling support.",
  },
  {
    n: "02",
    title: "Choose Eucalypt Therapy",
    text: "Once your counselling is approved, nominate Eucalypt Therapy as your provider — we will take it from there.",
  },
  {
    n: "03",
    title: "No out-of-pocket cost",
    text: "We bill Victims Services directly for your approved sessions, so there is nothing for you to pay.",
  },
];

const HELP_WITH = [
  "Trauma and PTSD",
  "Anxiety and hypervigilance",
  "Grief and loss",
  "Impacts of family or domestic violence",
  "Sleep difficulties and nightmares",
  "Rebuilding safety and trust",
];

export default function VictimsAssist() {
  return (
    <ServiceLayout
      eyebrow="Services"
      title="NSW Victims Assist Therapy"
      lead="If you have been affected by a violent crime in NSW, you may be eligible for counselling fully funded through Victims Services — with no out-of-pocket cost to you."
      image="https://images.unsplash.com/photo-1509414556967-312906f278a0?crop=entropy&cs=srgb&fm=jpg&q=85"
      imageAlt="Tall trees reaching toward soft light"
    >
      <ContentSection eyebrow="Who can access" title="Funded counselling for">
        <TickList items={WHO} />
        <Reveal delay={0.1}>
          <p className="mt-8 max-w-3xl text-base leading-relaxed text-mutedsoft">
            Eligibility is determined by Victims Services. If you are unsure whether you qualify, contact us and we
            can point you in the right direction.
          </p>
        </Reveal>
      </ContentSection>

      <ContentSection eyebrow="How it works" title="Three simple steps" pale>
        <div className="grid gap-10 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="border-t-2 border-euca-line pt-6">
                <span className="font-serif text-4xl font-medium italic text-euca-sage/70">{s.n}</span>
                <h3 className="mt-4 font-serif text-xl font-medium text-euca-dark">{s.title}</h3>
                <p className="mt-2 text-base leading-relaxed text-mutedsoft">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <p className="mt-10 max-w-3xl text-base leading-relaxed text-mutedsoft">
            Learn more or apply at{" "}
            <a
              href="https://www.victimservices.nsw.gov.au"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-euca hover:underline"
              data-testid="victims-services-link"
            >
              victimservices.nsw.gov.au
            </a>{" "}
            or call the Victims Access Line on{" "}
            <a href="tel:1800633063" className="font-semibold text-euca hover:underline" data-testid="victims-access-line">
              1800 633 063
            </a>
            .
          </p>
        </Reveal>
      </ContentSection>

      <ContentSection eyebrow="Support for" title="We may be able to help with">
        <TickList items={HELP_WITH} />
        <Reveal delay={0.1}>
          <div className="mt-10 max-w-3xl rounded-2xl border border-euca-line bg-white p-7 sm:p-8" data-testid="victims-private-note">
            <p className="text-base leading-relaxed text-charcoal/85">
              Not approved through Victims Services, or outside NSW? Sessions are also available privately at{" "}
              <span className="font-semibold">$200 per 50-minute session</span>, with a{" "}
              <span className="font-semibold">Medicare rebate of $89.50</span> available with an eligible GP
              referral. See{" "}
              <Link to="/fees-funding" className="font-semibold text-euca hover:underline" data-testid="victims-fees-link">
                Fees &amp; Funding
              </Link>
              .
            </p>
          </div>
        </Reveal>
      </ContentSection>
    </ServiceLayout>
  );
}
